import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InfoBanner, ReactSelect, SpinnerLoader, TableHeader } from "@services/ui-components";
import { ServicePick } from "../../ui-components/CategotyPick/ServicePick";
import { Button, Col, Row } from "react-bootstrap";
import DateView from "@services/ui-components/src/DatePicker/DatePicker";
import { StatusPicker } from "./StatusPicker/StatusPicker";
import {
    ReactSelectInterface,
    recordFormValueType,
    StatusPickerInner,
    ValidationRecordError,
} from "../../../interfaces/interfaces";
import { ServiceCategoryNewProxy, ServiceProxy } from "@me-team/host/main/graphql/types";
import {
    useCreateRecordMutation,
    useGetBookingInfoQuery,
    useGetClientInfoForRecordQuery,
    useGetDatesForRecordQuery,
    useGetDatesTimesForRecordQuery,
    useGetEmployeesForRecordQuery,
    useGetServiceCategoryForRecordQuery,
    useGetStatusListForRecordQuery,
    useUpdateRecordMutation,
} from "@me-team/host/booking/addRecord/graphql/addRecord.hooks";
import {
    useCheckBranchDataForRecordQuery,
    useGetModuleListForRecordQuery,
    useGetTechnologicalMapsServiceQuery,
} from "@me-team/host/main/addRecord/graphql/addRecord.hooks";
import { AddRecordService } from "../../../services/addRecord/AddRecordService";
import { DateTime } from "luxon";
import { apolloClientBooking } from "@me-team/host/src/apollo/apollo-client";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { ErrorMessage, Field, FieldProps, Form, Formik, FormikProps } from "formik";
import { BookingDate, BookingTime, EmployeeBookingProxy } from "@me-team/host/booking/graphql/types";
import { TimePickerWrapper } from "./TimePickerWrapper/TimePickerWrapper";
import { NotificationSelect } from "./NotificationSelect/NotificationSelect";
import { ClearValueBtn } from "./ClearValueBtn/ClearValueBtn";
import { AllModulesTagName, bookingStatusIds, employeeWorkStatus } from "../../../utils/enums";
import { Debounce } from "../../../services/Debounce";
import { PhoneInput } from "react-international-phone";
import { Link, useNavigate, useParams } from "react-router-dom";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import NotificationsModal from "../../Logbook/NotificationsForNewCompany/NotificationsModal";
import { SaleProducts } from "../../index";
import { useToast } from "../../../hooks/useToast";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { BookingDataForSale } from "../../Warehouse/WarehousesOperations/types";
import {
    useCheckBookingForSaleQuery,
    useInfoForSalesTooltipQuery,
} from "@me-team/host/main/salesOperation/graphql/salesOperation.hooks";
import SaleOfProductsButton from "./SaleOfProductsButton/SaleOfProductsButton";
import { useModulesAccess } from "@me-team/host/src/utils/useModulesAccess";
import { useCloseTooltipsOnModalOpen } from "../../../hooks/useCloseTooltipsOnModalOpen";
import DotLoadingComponent from "../../ui-components/DotLoadingComponent/DotLoadingComponent";
import { useAddRecordAccess } from "./useAddRecordAccess";
import { SelectEmptyState } from "./SelectEmptyState";
import {
    AddRecordEmptyStateActionType,
    AddRecordEmptyStateConfig,
    getMasterEmptyStateConfig,
    getServiceEmptyStateConfig,
} from "./addRecordEmptyState";

export const AddRecord: React.FC = () => {
    const { t: translate } = useTranslation();
    const { bookingUuid, branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [serviceCategoryList, setServiceList] = useState<ServiceCategoryNewProxy[]>(null);
    const [companyCurrencySign, setCompanyCurrencySign] = useState<string>(null);
    const [statusList, setStatusList] = useState<StatusPickerInner[]>(null);
    const [pickedServiceId, setPickedServiceId] = useState<number>(null);
    const [technologicalMaps, setTechnologicalMaps] = useState<ReactSelectInterface[]>([]);
    const [selectedTechnologicalMaps, setSelectedTechnologicalMaps] = useState<ReactSelectInterface[]>([]);
    const [employeeList, setEmployeeList] = useState<EmployeeBookingProxy[]>(null);
    const [pickedEmployeeId, setPickedEmployeeId] = useState<number>(null);
    const [bookingDates, setBookingDates] = useState<BookingDate[]>(null);
    const [pickedDate, setPickedDate] = useState<Date>(null);
    const [pickedTime, setPickedTime] = useState<DateTime>(null);
    const [bookingTimes, setBookingTimes] = useState<BookingTime[]>(null);
    const [isWithWarehouse, setIsWithWarehouse] = useState<boolean>(null);
    const navigate = useNavigate();
    const [initialValues, setInitialValues] = useState<recordFormValueType>(null);
    const timePickerDivRef = useRef<HTMLDivElement>(null);
    const [toggleProductSaleModal, setToggleProductSaleModal] = useState(false);
    const { showToast, toastText, setShowToast, toggleShowToast } = useToast();
    const [checkModalShow, setCheckModalShow] = useState<boolean>(false);
    const enabledWarehouseModule: boolean = useModulesAccess([AllModulesTagName.WAREHOUSES]);
    const [operationNumber, setOperationNumber] = useState<number>(null);
    const { tooltipResetKey, resetTooltips } = useCloseTooltipsOnModalOpen();
    const [durationMoreThenGap, setDurationMoreThenGap] = useState<boolean>(false);
    const [isAnyActiveServices, setIsAnyActiveServices] = useState<boolean>(null);
    const [hasActiveEmployees, setHasActiveEmployees] = useState<boolean>(null);
    const formikRef = useRef<FormikProps<recordFormValueType>>(null);

    const isEditMode = !!bookingUuid;

    const closeCheckModal = () => setCheckModalShow(false);

    const { loading: loadingCheckBranchData } = useCheckBranchDataForRecordQuery({
        variables: { companyId: Number(currentCompanyId), branchId: Number(branchId) },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        fetchPolicy: "cache-and-network",
        onCompleted: (data) => {
            const isAnyActiveServiceEmployeeRelations =
                data.user.company.branches[0].isAnyActiveServiceEmployeeRelations;
            const isAnyActiveServices = data.user.company.branches[0].isAnyActiveServices;
            const isAnyWorkScheduleForWorkingEmployees =
                data.user.company.branches[0].isAnyWorkScheduleForWorkingEmployees;
            const activeEmployeesTotalCount = data.user.company.branches[0].employeesPaginated?.totalCount ?? 0;
            setIsAnyActiveServices(isAnyActiveServices);
            setHasActiveEmployees(activeEmployeesTotalCount > 0);
            if (!isAnyActiveServiceEmployeeRelations || !isAnyActiveServices || !isAnyWorkScheduleForWorkingEmployees) {
                setCheckModalShow(true);
            }
        },
        onError: (error: ApolloError) => {
            console.error(error);
        },
    });

    const bookingDataForSale: BookingDataForSale = useMemo(
        () => ({
            employee: initialValues?.employeeId,
            clientName: initialValues?.clientName,
            clientPhone: initialValues?.phone,
            clientEmail: initialValues?.email,
        }),
        [initialValues?.employeeId, initialValues?.clientName, initialValues?.phone, initialValues?.email]
    );

    const {
        data: checkBookingForSaleData,
        loading: checkBookingForSaleLoading,
        refetch: checkBookingForSaleRefetch,
    } = useCheckBookingForSaleQuery({
        skip: !currentCompanyId || !bookingUuid,
        fetchPolicy: "network-only",
        variables: {
            companyId: Number(currentCompanyId),
            branchId: Number(branchId),
            bookingUuid,
        },
        onError: (error: ApolloError) => {
            console.error(error);
        },
    });
    const isBookingSale =
        !!checkBookingForSaleData?.user?.company?.branchesPaginated?.branches?.[0]
            .storageProductRelationHistoryPaginated?.entities?.[0]?.booking?.id;

    const handleToggleProductSaleModal = () => {
        resetTooltips();
        setToggleProductSaleModal((prevState) => !prevState);
    };

    const {
        data: infoForSalesTooltipData,
        loading: infoForSalesTooltipLoading,
        refetch: infoForSalesTooltipRefetch,
    } = useInfoForSalesTooltipQuery({
        variables: {
            companyId: Number(currentCompanyId),
            branchId: Number(branchId),
            uuid: bookingUuid,
        },
        skip: !currentCompanyId || !bookingUuid,
        onError: (error: ApolloError) => {
            console.error(error);
        },
    });
    const infoForSalesTooltip = infoForSalesTooltipData?.user?.company?.branches?.[0]?.bookingsList?.[0];
    const isInfoForSalesTooltip =
        !!infoForSalesTooltip?.saleActionProductsAmount && !!infoForSalesTooltip?.saleActionDate;

    const { loading: loadingService } = useGetServiceCategoryForRecordQuery({
        skip: !branchId,
        variables: {
            branchId: parseInt(branchId),
            employeeId: pickedEmployeeId,
            dateTime: pickedDate && pickedTime ? DateTime.fromJSDate(pickedDate).toFormat("yyyy-MM-dd HH:mm:ss") : null,
            bookingUuid: bookingUuid ? bookingUuid : null,
        },
        fetchPolicy: "network-only",
        client: apolloClientBooking,
        onCompleted: (data) => {
            const enableServiceCategories = data.serviceCategories
                .filter(({ deleted }) => !deleted)
                .map(({ services, ...category }) => ({
                    ...category,
                    services: services.filter(({ deleted }) => !deleted),
                }));
            if (bookingUuid) {
                setServiceList(data.serviceCategories);
            } else {
                setServiceList(enableServiceCategories);
            }
        },
    });

    const { data: statusData, loading: loadingStatus } = useGetStatusListForRecordQuery({
        fetchPolicy: "network-only",
        client: apolloClientBooking,
    });

    const { data: moduleData, loading: loadingModule } = useGetModuleListForRecordQuery({
        skip: !currentCompanyId,
        fetchPolicy: "network-only",
        variables: { companyId: parseInt(currentCompanyId) },
        onCompleted: (data) => {
            setCompanyCurrencySign(data.user.company.currency.currencySign);
            const WAREHOUSE_NAME = "Warehouses";
            const isWithWarehouse = data.user.company.modules.find((module) => module.name === WAREHOUSE_NAME);
            setIsWithWarehouse(isWithWarehouse?.active);
        },
    });

    const { refetch: refetchEmployees, loading: loadingEmployees } = useGetEmployeesForRecordQuery({
        skip: !branchId,
        variables: {
            branchId: branchId,
            online: false,
            serviceId: pickedServiceId ? String(pickedServiceId) : null,
            dateTime: pickedDate && pickedTime ? DateTime.fromJSDate(pickedDate).toFormat("yyyy-MM-dd HH:mm:ss") : null,
            bookingId: bookingUuid ? bookingUuid : null,
        },
        client: apolloClientBooking,
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            if (bookingUuid) {
                setEmployeeList(data.employees);
            } else {
                const enableEmployees = data.employees.filter(
                    (employee) => employee.status.id !== employeeWorkStatus.FIRED
                );
                setEmployeeList(enableEmployees);
            }
        },
    });

    const { refetch: refetchDates, loading: loadingDates } = useGetDatesForRecordQuery({
        skip: !branchId,
        variables: {
            branchId: branchId,
            online: false,
            serviceId: pickedServiceId ? String(pickedServiceId) : null,
            employeeId: pickedEmployeeId ? String(pickedEmployeeId) : null,
            bookingId: bookingUuid ? bookingUuid : null,
        },
        client: apolloClientBooking,
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            setBookingDates(data.bookingDates);
        },
    });

    let checkTimesByDate: (bookingTimes: BookingTime[], pikedDate: Date) => void;

    const { loading: loadingTimes } = useGetDatesTimesForRecordQuery({
        skip: !pickedDate || !branchId,
        variables: {
            branchId: branchId,
            online: false,
            serviceId: pickedServiceId ? String(pickedServiceId) : null,
            employeeId: pickedEmployeeId ? String(pickedEmployeeId) : null,
            date: DateTime.fromJSDate(pickedDate).toFormat("yyyy-MM-dd"),
            bookingId: bookingUuid ? bookingUuid : null,
        },
        client: apolloClientBooking,
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            checkTimesByDate(data.bookingTimes, pickedDate);
            setBookingTimes(data.bookingTimes);
        },
    });

    const { refetch: refetchClientInfo, loading: loadingClientInfo } = useGetClientInfoForRecordQuery({
        skip: true,
        variables: { branchId: branchId, searchEmail: "", searchPhone: "" },
        client: apolloClientBooking,
        fetchPolicy: "network-only",
    });

    const {
        data: technologicalMapsData,
        loading: loadingTechnologicalMaps,
        refetch: refetchTechnologicalMaps,
    } = useGetTechnologicalMapsServiceQuery({
        skip: !currentCompanyId,
        variables: { companyId: parseInt(currentCompanyId), inStockProducts: true, branchId: Number(branchId) },
        onCompleted: (data) => {
            setTechnologicalMaps(
                data.user.company.technologicalMaps.technologicalMaps.map((technologicalMap) => {
                    return {
                        label: technologicalMap.name,
                        value: technologicalMap.id,
                    };
                })
            );
        },
    });

    const { loading: loadingSelectedTechnologicalMaps } = useGetTechnologicalMapsServiceQuery({
        skip: !pickedServiceId || !currentCompanyId,
        variables: {
            branchId: Number(branchId),
            serviceId: bookingUuid ? null : pickedServiceId,
            companyId: +currentCompanyId,
            bookingUuid: bookingUuid || null,
        },
        onCompleted: (data) => {
            if (data.user.company.technologicalMaps.technologicalMaps.length !== 0) {
                const selectedMaps = data.user.company.technologicalMaps.technologicalMaps.map((technologicalMap) => {
                    return {
                        label: technologicalMap.name,
                        value: technologicalMap.id,
                    };
                });

                setSelectedTechnologicalMaps(selectedMaps);

                const filteredMaps = technologicalMaps
                    .map((technologicalMap) => {
                        return {
                            label: technologicalMap.label,
                            value: technologicalMap.value,
                        };
                    })
                    .filter((map) => !selectedMaps.some((selectedMap) => selectedMap.value === map.value));
                setTechnologicalMaps(filteredMaps);
            } else {
                setSelectedTechnologicalMaps([]);
                refetchTechnologicalMaps().then((techCardsData) => {
                    setTechnologicalMaps(
                        techCardsData.data.user.company.technologicalMaps.technologicalMaps.map((technologicalMap) => {
                            return {
                                label: technologicalMap.name,
                                value: technologicalMap.id,
                            };
                        })
                    );
                });
            }
        },
    });

    const { data: bookingData, loading: loadingBookingData } = useGetBookingInfoQuery({
        skip: !bookingUuid || !technologicalMapsData,
        variables: { branchId: Number(branchId), uuid: bookingUuid, online: false },
        fetchPolicy: "network-only",
        client: apolloClientBooking,
        onCompleted: (data) => {
            data.booking.operationNumberForWriteOff && setOperationNumber(data.booking.operationNumberForWriteOff);

            setDurationMoreThenGap(data.booking.durationMoreThenGap);
            const selectedRecordMaps = data.booking.technologicalMaps.map((technologicalMap) => {
                return {
                    label: technologicalMap.name,
                    value: technologicalMap.id,
                };
            });
            setSelectedTechnologicalMaps(selectedRecordMaps);
            const filteredMaps = technologicalMapsData.user.company.technologicalMaps.technologicalMaps
                .map((technologicalMap) => {
                    return {
                        label: technologicalMap.name,
                        value: technologicalMap.id,
                    };
                })
                .filter((map) => !selectedRecordMaps.some((selectedMap) => selectedMap.value === map.value));
            setTechnologicalMaps(filteredMaps);
            const combinedDateTime = DateTime.fromFormat(
                `${data.booking.date} ${data.booking.time}`,
                "yyyy-MM-dd HH:mm:ss"
            );
            setPickedDate(combinedDateTime.toJSDate());
            setPickedTime(DateTime.fromFormat(data.booking.time, "HH:mm:ss"));
            setPickedEmployeeId(data.booking.employee.id);
        },
    });

    const bookingEmployeeId = bookingData?.booking?.employee?.id ?? null;
    const {
        isAddRecordAccessPending,
        normalizedCurrentBranchEmployeeId,
        shouldRestrictCreateBookingToSelf,
        canCreateBookingForEmployee,
        canEditBookingByAccess,
        canEditBookingStatusByAccess,
        canViewHistoryByAccess,
        canWriteOffByAccess,
        canManageSaleByAccess,
        canCreateService,
        canCreateEmployee,
        canViewServiceList,
        canViewEmployeeList,
    } = useAddRecordAccess({
        bookingEmployeeId,
        bookingUuid,
        isBookingSale,
    });
    const canEditStatusInForm = !isEditMode || canEditBookingStatusByAccess;

    const handleCreateServiceClick = () => {
        navigate(`/services/service/${currentCompanyId}/new/${branchId}`);
    };

    const handleCreateMasterClick = () => {
        navigate(`/employees-list/add-employee/${branchId}`);
    };

    const handleGoToServiceListClick = () => {
        navigate(`/services/service/${currentCompanyId}/service-list/${branchId}`);
    };

    const handleGoToMasterListClick = () => {
        navigate(`/employees-list/${branchId}`);
    };

    const getEmptyStateActionHandler = (actionType: AddRecordEmptyStateActionType) => {
        switch (actionType) {
            case "createService":
                return handleCreateServiceClick;
            case "createMaster":
                return handleCreateMasterClick;
            case "goToServiceList":
                return handleGoToServiceListClick;
            case "goToMasterList":
                return handleGoToMasterListClick;
            default:
                return undefined;
        }
    };

    const renderSelectEmptyState = (config?: AddRecordEmptyStateConfig): React.ReactNode => {
        if (!config) return undefined;

        const actionHandler = config.actionType ? getEmptyStateActionHandler(config.actionType) : undefined;

        return (
            <SelectEmptyState
                message={translate(config.messageKey)}
                actionLabel={config.actionLabelKey ? translate(config.actionLabelKey) : undefined}
                onActionClick={actionHandler}
                actionIconClassName={config.actionIconClassName}
            />
        );
    };

    const serviceEmptyState = renderSelectEmptyState(
        getServiceEmptyStateConfig({
            isAnyActiveServices,
            canCreateService,
            canViewServiceList,
        })
    );

    const masterEmptyState = renderSelectEmptyState(
        getMasterEmptyStateConfig({
            shouldRestrictCreateBookingToSelf,
            hasActiveEmployees,
            canCreateEmployee,
            canViewEmployeeList,
        })
    );

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const dateParam = params.get("date");
        const employeeIdParam = params.get("employeeId");
        const timeParam = params.get("time");

        let dateTime: DateTime = null;

        if (dateParam && timeParam) {
            dateTime = DateTime.fromFormat(`${dateParam} ${timeParam}`, "yyyy-MM-dd HH:mm");
            setPickedDate(dateTime.toJSDate());
            setPickedTime(dateTime);
        }

        if (statusData && !isAddRecordAccessPending) {
            const defaultEmployeeId = AddRecordService.selectedDefaultEmployee(
                shouldRestrictCreateBookingToSelf ? String(normalizedCurrentBranchEmployeeId ?? "") : employeeIdParam,
                shouldRestrictCreateBookingToSelf ? normalizedCurrentBranchEmployeeId : null
            );

            if (defaultEmployeeId) {
                setPickedEmployeeId(defaultEmployeeId);
            }
            const initialValuesFromUrl: recordFormValueType = {
                service: { categoryId: null, serviceId: null, duration: null },
                technologicalMap: [],
                employeeId: defaultEmployeeId,
                price: 0,
                pickedDate: dateTime ? dateTime.toJSDate() : null,
                startTime:
                    timeParam && dateTime
                        ? DateTime.fromFormat(timeParam, "HH:mm").set({
                              year: dateTime.year,
                              month: dateTime.month,
                              day: dateTime.day,
                          })
                        : null,
                endTime: null,
                status: {
                    isSelected: false,
                    statusId: AddRecordService.statusToShow(statusData.bookingStatus, dateTime ? dateTime : null).find(
                        (status) => status.useByDefault
                    ).id,
                },
                phone: "",
                email: "",
                clientName: "",
                clientMiddleName: "",
                clientLastName: "",
                notes: "",
                notification: { isUserPick: false, date: null },
            };

            if (!bookingData?.booking) {
                setInitialValues(initialValuesFromUrl);
            } else {
                setInitialValues(AddRecordService.generateInitialValuesFromBooking(bookingData.booking));
                setPickedServiceId(bookingData.booking.service.id);
            }
        }
    }, [
        bookingUuid,
        bookingData,
        isAddRecordAccessPending,
        isWithWarehouse,
        normalizedCurrentBranchEmployeeId,
        shouldRestrictCreateBookingToSelf,
        statusData,
    ]);

    useEffect(() => {
        if (!shouldRestrictCreateBookingToSelf || normalizedCurrentBranchEmployeeId === null) {
            return;
        }
        if (pickedEmployeeId !== normalizedCurrentBranchEmployeeId) {
            setPickedEmployeeId(normalizedCurrentBranchEmployeeId);
        }
        setInitialValues((prevState) => {
            if (!prevState || prevState.employeeId === normalizedCurrentBranchEmployeeId) {
                return prevState;
            }
            return {
                ...prevState,
                employeeId: normalizedCurrentBranchEmployeeId,
            };
        });

        if (formikRef.current?.values.employeeId !== normalizedCurrentBranchEmployeeId) {
            formikRef.current?.setFieldValue("employeeId", normalizedCurrentBranchEmployeeId, false);
        }
    }, [normalizedCurrentBranchEmployeeId, pickedEmployeeId, shouldRestrictCreateBookingToSelf]);

    const [createRecord] = useCreateRecordMutation({
        client: apolloClientBooking,
        fetchPolicy: "network-only",
    });

    const [updateRecord] = useUpdateRecordMutation({
        client: apolloClientBooking,
        fetchPolicy: "network-only",
    });

    const scopedEmployeeList = useMemo(() => {
        if (!employeeList) {
            return [];
        }
        if (!shouldRestrictCreateBookingToSelf) {
            return employeeList;
        }
        if (normalizedCurrentBranchEmployeeId === null) {
            return [];
        }

        return employeeList.filter((employee) => employee.id === normalizedCurrentBranchEmployeeId);
    }, [employeeList, normalizedCurrentBranchEmployeeId, shouldRestrictCreateBookingToSelf]);
    const shouldShowSaleButton = !!bookingUuid && enabledWarehouseModule && canManageSaleByAccess;
    const shouldShowWriteOffCreateLink =
        !!bookingUuid &&
        !!bookingData &&
        bookingData.booking.status.id === bookingStatusIds.COMPLETED &&
        !operationNumber &&
        enabledWarehouseModule &&
        canWriteOffByAccess;
    const shouldShowWriteOffEditLink = !!bookingUuid && !!operationNumber && canWriteOffByAccess;

    useEffect(() => {
        if (moduleData && statusData) {
            const currentDate = pickedDate ? DateTime.fromJSDate(pickedDate) : DateTime.now().plus({ second: 10 });
            setStatusList(
                AddRecordService.statusToShow(statusData.bookingStatus, currentDate, bookingData?.booking.status.id)
            );
        }
    }, [statusData, moduleData, pickedDate, isWithWarehouse, bookingData]);

    const isDisabledDate = (date: Date): boolean => {
        if (!bookingDates) {
            return true;
        }

        if (bookingDates.length === 0) {
            return true;
        }

        return bookingDates.some(({ start, end }) => {
            const startDate = new Date(start).setHours(0, 0, 0);
            const endDate = new Date(end).setHours(0, 0, 0);

            return date.getTime() >= startDate && date.getTime() <= endDate;
        });
    };

    const handleSubmit = async (
        values: recordFormValueType,
        {
            setSubmitting,
            setValues,
            setErrors,
        }: {
            setSubmitting: (isSubmitting: boolean) => void;
            setFieldError: (field: string, message: string) => void;
            setValues: (values: recordFormValueType) => void;
            setErrors: (errors: { [field: string]: string }) => void;
        }
    ) => {
        const serverErrorHandling = async (error: ApolloError) => {
            const currentError: ValidationRecordError = AddRecordService.submitRecordErrors(error);

            if (currentError.date) {
                setValues({
                    ...values,
                    pickedDate: null,
                    startTime: null,
                    endTime: null,
                });
                setPickedDate(null);
                setPickedTime(null);
                const errorMessage: string = translate(`${currentError.date[0].message}`);

                try {
                    const refetchResult = await refetchDates();
                    setBookingDates(refetchResult.data.bookingDates);
                } catch (refetchError) {
                    console.error("Refetch error", refetchError);
                }
                setErrors({
                    pickedDate: errorMessage,
                    startTime: "",
                });
            }

            if (currentError.employee) {
                setValues({
                    ...values,
                    employeeId: null,
                });
                setPickedEmployeeId(null);
                const errorMessage: string = translate(`${currentError.employee[0].message}`);
                try {
                    const refetchResult = await refetchEmployees();
                    setEmployeeList(refetchResult.data.employees);
                } catch (refetchError) {
                    console.error("Refetch error", refetchError);
                }
                setErrors({
                    employee: errorMessage,
                });
            }

            if (currentError.service) {
                setValues({
                    ...values,
                    service: { categoryId: null, serviceId: null, duration: null },
                    technologicalMap: [],
                });

                const errorMessage: string = translate(`${currentError.service[0].message}`);

                setPickedServiceId(null);
                setSelectedTechnologicalMaps([]);

                try {
                    const refetchResult = await refetchTechnologicalMaps();
                    setTechnologicalMaps(
                        refetchResult.data.user.company.technologicalMaps.technologicalMaps.map((technologicalMap) => {
                            return {
                                label: technologicalMap.name,
                                value: technologicalMap.id,
                            };
                        })
                    );
                } catch (refetchError) {
                    console.error("Refetch error", refetchError);
                }

                setErrors({
                    service: errorMessage,
                });
            }
        };

        if (!bookingUuid && !canCreateBookingForEmployee(values.employeeId)) {
            setSubmitting(false);
            return;
        }

        if (bookingUuid && !canEditBookingByAccess) {
            setSubmitting(false);
            return;
        }

        const recordInput = AddRecordService.generateRecordInput(
            values,
            parseInt(branchId),
            selectedTechnologicalMaps,
            bookingUuid
        );
        if (bookingUuid) {
            await updateRecord({
                variables: {
                    uuid: bookingUuid,
                    branchId: parseInt(branchId),
                    employeeId: values.employeeId,
                    input: recordInput,
                },
                onCompleted: (data) => {
                    const isNeedToWriteOff: boolean =
                        data.updateAdminBooking.status.id === bookingStatusIds.COMPLETED &&
                        isWithWarehouse &&
                        !data.updateAdminBooking.isWriteOffCompleted;

                    navigate(`/logbook/journal/${branchId}`, {
                        state: {
                            text: translate("Saved"),
                            toast: true,
                            recordId: data.updateAdminBooking.id,
                            recordUuid: data.updateAdminBooking.uuid,
                            isNeedToWriteOff: isNeedToWriteOff,
                        },
                    });
                    setSubmitting(false);
                },
                onError: async (error) => {
                    await serverErrorHandling(error);
                    setSubmitting(false);
                },
            });
        } else {
            await createRecord({
                variables: {
                    branchId: parseInt(branchId),
                    employeeId: values.employeeId,
                    input: recordInput,
                },
                onCompleted: (data) => {
                    const isNeedToWriteOff: boolean =
                        data.createAdminBooking.status.id === bookingStatusIds.COMPLETED && isWithWarehouse;

                    navigate(`/logbook/journal/${branchId}`, {
                        state: {
                            text: translate("Created"),
                            toast: true,
                            recordId: data.createAdminBooking.id,
                            recordUuid: data.createAdminBooking.uuid,
                            isNeedToWriteOff: isNeedToWriteOff,
                        },
                    });
                    setSubmitting(false);
                },
                onError: async (error) => {
                    await serverErrorHandling(error);
                    setSubmitting(false);
                },
            });
        }
    };

    const tooltipIconNameTitle = () => {
        if (bookingUuid) {
            return bookingData?.booking.fromOnline ? "globe" : "layout-text-window-reverse";
        }
    };

    const tooltipTextTitle = () => {
        if (bookingUuid) {
            const createdBy = bookingData?.booking.fromOnline ? translate("Online Booking") : translate("Logbook");
            return `${translate("Created by")} ${createdBy}`;
        }
    };

    const loading =
        loadingStatus ||
        loadingClientInfo ||
        loadingTimes ||
        loadingService ||
        loadingDates ||
        loadingEmployees ||
        loadingTechnologicalMaps ||
        loadingSelectedTechnologicalMaps ||
        loadingModule ||
        isAddRecordAccessPending ||
        checkBookingForSaleLoading ||
        infoForSalesTooltipLoading ||
        loadingCheckBranchData ||
        loadingBookingData;

    return (
        <>
            {loading && (
                <div className="d-flex align-items-center justify-content-center position-fixed top-50 end-50 z-3">
                    <div className="preloader__spinner"></div>
                </div>
            )}

            {initialValues && (
                <>
                    <Formik
                        validationSchema={AddRecordService.recordValidation(translate)}
                        initialValues={initialValues}
                        enableReinitialize
                        validateOnChange={true}
                        validateOnBlur={false}
                        innerRef={formikRef}
                        onSubmit={(values, actions) => handleSubmit(values, actions)}
                    >
                        {({
                            values,
                            setFieldValue,
                            isSubmitting,
                            errors,
                            touched,
                            setValues,
                            setFieldError,
                            setFieldTouched,
                        }) => {
                            checkTimesByDate = (bookingTimes: BookingTime[], pickedDate: Date) => {
                                setPickedTime(values.startTime);

                                if (values.startTime && bookingTimes.length !== 0) {
                                    const isValid = AddRecordService.checkTimesRangeInCurrentDate(
                                        pickedDate,
                                        bookingTimes,
                                        values.startTime
                                    );

                                    if (!isValid) {
                                        setPickedTime(null);
                                        setFieldTouched("startTime", true, false);
                                        setValues(
                                            {
                                                ...values,
                                                startTime: null,
                                                endTime: null,
                                            },
                                            false
                                        );

                                        setFieldError("startTime", translate("Select an available time"));
                                    }
                                }
                            };

                            const onServiceClearValue = async () => {
                                setPickedServiceId(null);

                                const updatedValues: recordFormValueType = {
                                    ...values,
                                    service: { categoryId: null, serviceId: null, duration: null },
                                    price: 0,
                                    technologicalMap: [],
                                };

                                try {
                                    const refetchResult = await refetchTechnologicalMaps();
                                    setTechnologicalMaps(
                                        refetchResult.data.user.company.technologicalMaps.technologicalMaps.map(
                                            (technologicalMap) => {
                                                return {
                                                    label: technologicalMap.name,
                                                    value: technologicalMap.id,
                                                };
                                            }
                                        )
                                    );
                                } catch (refetchError) {
                                    console.error(refetchError);
                                }

                                setSelectedTechnologicalMaps([]);
                                setValues(updatedValues);
                            };

                            const onEmployeeClearValue = () => {
                                setPickedEmployeeId(null);
                                const updatedValues: recordFormValueType = {
                                    ...values,
                                    employeeId: null,
                                    service: { ...values.service, duration: null },
                                    endTime: null,
                                    price: 0,
                                };

                                setValues(updatedValues);
                            };

                            const onDateTimeClearValues = () => {
                                setPickedDate(null);
                                setPickedTime(null);
                                setDurationMoreThenGap(false);

                                const updatedValues: recordFormValueType = {
                                    ...values,
                                    pickedDate: null,
                                    startTime: null,
                                    endTime: null,
                                };

                                setValues(updatedValues);
                            };

                            const onServicePickHandler = (serviceId: number, isPicked: boolean, categoryId: number) => {
                                if (isPicked) {
                                    setPickedServiceId(serviceId);

                                    const currentCategory: ServiceCategoryNewProxy = serviceCategoryList.find(
                                        (category) => category.id === categoryId
                                    );
                                    const currentService: ServiceProxy = currentCategory.services.find(
                                        (service) => service.id === serviceId
                                    );
                                    const updatedEndTime = values.startTime
                                        ? AddRecordService.calcEndTime(
                                              values.startTime,
                                              values.employeeId
                                                  ? currentService.employeeServiceRelations[0].duration
                                                  : currentService.duration
                                          )
                                        : null;

                                    const updatedValues: recordFormValueType = {
                                        ...values,
                                        service: {
                                            categoryId: categoryId,
                                            serviceId: serviceId,
                                            duration: values.employeeId
                                                ? currentService.employeeServiceRelations[0].duration
                                                : currentService.duration,
                                        },
                                        price: values.employeeId
                                            ? currentService.employeeServiceRelations[0].price
                                            : values.price,
                                        endTime: updatedEndTime,
                                    };

                                    setValues(updatedValues);
                                } else {
                                    onServiceClearValue();
                                }
                            };
                            const deleteSelectedTechnologicalMaps = (technologicalMap: ReactSelectInterface) => {
                                setSelectedTechnologicalMaps(
                                    selectedTechnologicalMaps.filter((map) => map.value !== technologicalMap.value)
                                );

                                setFieldValue(
                                    "technologicalMap",
                                    values.technologicalMap.filter(
                                        (technologicalMapItem) => technologicalMapItem.value !== technologicalMap.value
                                    )
                                );

                                setTechnologicalMaps(
                                    [...technologicalMaps, technologicalMap].sort((a, b) =>
                                        a.label.localeCompare(b.label)
                                    )
                                );
                            };

                            const onTimePickHandler = (newDateTime: DateTime) => {
                                const updatedPickedDate = new Date(pickedDate);
                                const clearSecond = newDateTime.set({ second: 0 });
                                updatedPickedDate.setHours(clearSecond.hour, clearSecond.minute, 0, 0);

                                const updatedEndTime = values.service.duration
                                    ? AddRecordService.calcEndTime(clearSecond, values.service.duration)
                                    : null;

                                const updateNotification: ReactSelectInterface = AddRecordService.notificationValueCalc(
                                    values.notification,
                                    DateTime.fromJSDate(updatedPickedDate),
                                    translate
                                );

                                const notificationToString = AddRecordService.notificationValueToString(
                                    updateNotification,
                                    DateTime.fromJSDate(updatedPickedDate)
                                );

                                const status: StatusPickerInner = AddRecordService.statusService(
                                    values.status,
                                    AddRecordService.statusToShow(
                                        statusData.bookingStatus,
                                        DateTime.fromJSDate(updatedPickedDate),
                                        bookingData?.booking.status.id
                                    )
                                );

                                const updatedValues: recordFormValueType = {
                                    ...values,
                                    pickedDate: updatedPickedDate,
                                    status: canEditStatusInForm
                                        ? { ...values.status, statusId: status.id }
                                        : values.status,
                                    startTime: clearSecond,
                                    endTime: updatedEndTime,
                                    notification: {
                                        ...values.notification,
                                        date: notificationToString,
                                    },
                                };

                                setValues(updatedValues);
                                setPickedTime(clearSecond);
                                setPickedDate(updatedPickedDate);
                                setDurationMoreThenGap(false);
                            };

                            const debouncedSendQuery = Debounce((field: "phone" | "email", value: string) => {
                                if (!value) return;
                                const phone: string = field === "phone" && value ? value : null;
                                const email: string = field === "email" && value ? value : null;
                                refetchClientInfo({
                                    branchId: branchId,
                                    searchPhone: phone,
                                    searchEmail: email,
                                })
                                    .then((response) => {
                                        if (response.data.client) {
                                            if (field === "email") {
                                                const clientInfo = {
                                                    clientName: response.data.client.name,
                                                    clientMiddleName: response.data.client.surname
                                                        ? response.data.client.surname
                                                        : "",
                                                    clientLastName: response.data.client.patronymic
                                                        ? response.data.client.patronymic
                                                        : "",
                                                    clientPhone: response.data.client.phone
                                                        ? response.data.client.phone
                                                        : "",
                                                };

                                                setValues({
                                                    ...values,
                                                    email: value,
                                                    phone: clientInfo.clientPhone,
                                                    clientName: clientInfo.clientName,
                                                    clientMiddleName: clientInfo.clientMiddleName,
                                                    clientLastName: clientInfo.clientLastName,
                                                });
                                            }

                                            if (field === "phone") {
                                                const clientInfo = {
                                                    clientName: response.data.client.name,
                                                    clientMiddleName: response.data.client.surname
                                                        ? response.data.client.surname
                                                        : "",
                                                    clientLastName: response.data.client.patronymic
                                                        ? response.data.client.patronymic
                                                        : "",
                                                    clientEmail: response.data.client.email
                                                        ? response.data.client.email
                                                        : "",
                                                };

                                                setValues({
                                                    ...values,
                                                    email: clientInfo.clientEmail,
                                                    phone: value,
                                                    clientName: clientInfo.clientName,
                                                    clientMiddleName: clientInfo.clientMiddleName,
                                                    clientLastName: clientInfo.clientLastName,
                                                });
                                            }
                                        }
                                    })
                                    .catch((error) => {
                                        console.error(`Error fetching data for ${field}:`, error);
                                    });
                            }, 1000);

                            return (
                                serviceCategoryList &&
                                bookingDates &&
                                technologicalMaps &&
                                employeeList &&
                                statusList && (
                                    <>
                                        <InfoBanner>
                                            <p className="fs-7 mb-0">
                                                {translate(
                                                    "On this page you can create a new booking. Required fields are marked with an asterisk"
                                                )}{" "}
                                                (<span className="text-danger">*</span>)
                                            </p>
                                        </InfoBanner>

                                        <TableHeader
                                            title={
                                                bookingUuid ? translate("Edit booking") : translate("Add new booking")
                                            }
                                            paths={[
                                                {
                                                    path: `/logbook/journal/${branchId}`,
                                                    label: translate("Logbook Journal"),
                                                },
                                                bookingUuid
                                                    ? {
                                                          path: `/logbook/edit-record/${bookingUuid}/${branchId}`,
                                                          label: translate("Edit booking"),
                                                      }
                                                    : {
                                                          path: `/logbook/add-record/${branchId}`,
                                                          label: translate("Add new booking"),
                                                      },
                                            ]}
                                            tooltipIconName={tooltipIconNameTitle()}
                                            tooltipText={tooltipTextTitle()}
                                            buttons={
                                                <Row className="justify-content-lg-end">
                                                    {shouldShowSaleButton ? (
                                                        <SaleOfProductsButton
                                                            tooltipResetKey={tooltipResetKey}
                                                            bookingUuid={bookingUuid}
                                                            isInfoForSalesTooltip={isInfoForSalesTooltip}
                                                            infoForSalesTooltip={infoForSalesTooltip}
                                                            isBookingSale={isBookingSale}
                                                            onClick={handleToggleProductSaleModal}
                                                            translate={translate}
                                                        />
                                                    ) : null}
                                                    {shouldShowWriteOffCreateLink && (
                                                        <Col md={6} xl={5} className="mt-3 mt-lg-0">
                                                            <Link
                                                                to={`/products/write-off/company/${currentCompanyId}/branch/${branchId}/bookingUuid/${bookingUuid}`}
                                                                state={{ comeFrom: { isFromRecord: true } }}
                                                                className="btn btn-orange w-100 h-100"
                                                            >
                                                                {translate("Go to write-off")}
                                                            </Link>
                                                        </Col>
                                                    )}
                                                    {shouldShowWriteOffEditLink && (
                                                        <Col md={6} xl={5} className="mt-3 mt-lg-0">
                                                            <Link
                                                                to={`/products/write-off/company/${currentCompanyId}/branch/${branchId}/bookingUuid/${bookingUuid}/edit/operationNumber/${operationNumber}`}
                                                                state={{ comeFrom: { isFromRecord: true } }}
                                                                className="btn btn-orange w-100 h-100"
                                                            >
                                                                {translate("Go to write-off")}
                                                            </Link>
                                                        </Col>
                                                    )}
                                                </Row>
                                            }
                                        />
                                        <Form>
                                            <div className={"col-xl-9 pb-3"}>
                                                <section>
                                                    <h4 className={"mb-3"}>{translate("Service")}</h4>
                                                    <div className={"px-0 pe-lg-6 ms-0"}>
                                                        <Row>
                                                            <Col
                                                                xl={2}
                                                                className={"d-flex align-items-center mb-2 mb-xl-0"}
                                                            >
                                                                <label
                                                                    htmlFor="createNewBooking-service-field required-label"
                                                                    className="text-dark fs-7 fw-bold"
                                                                >
                                                                    {translate("Service name")}
                                                                </label>
                                                            </Col>
                                                            <Col xl={10} className={"position-relative"}>
                                                                <ServicePick
                                                                    serviceCategoryList={serviceCategoryList}
                                                                    companyCurrencySign={companyCurrencySign}
                                                                    selectedCategoryId={values.service.categoryId}
                                                                    selectedServiceId={values.service.serviceId}
                                                                    onServicePick={onServicePickHandler}
                                                                    isDisabled={
                                                                        (pickedDate &&
                                                                            !pickedTime &&
                                                                            !pickedServiceId) ||
                                                                        loadingService
                                                                    }
                                                                    employeeId={pickedEmployeeId}
                                                                    name={"service"}
                                                                    hasError={
                                                                        !!(
                                                                            touched.service?.serviceId &&
                                                                            errors.service?.serviceId
                                                                        )
                                                                    }
                                                                    id={"createNewBooking-service-field"}
                                                                    isLoading={
                                                                        loadingTimes ||
                                                                        loadingDates ||
                                                                        loadingEmployees ||
                                                                        loadingService
                                                                    }
                                                                    emptyState={serviceEmptyState}
                                                                />
                                                                {values.service.categoryId &&
                                                                    values.service.serviceId && (
                                                                        <ClearValueBtn
                                                                            onClearValue={onServiceClearValue}
                                                                        />
                                                                    )}
                                                            </Col>
                                                            <Col xl={{ span: 10, offset: 2 }}>
                                                                <ErrorMessage name="service.serviceId">
                                                                    {(msg) => (
                                                                        <p className="fs-7 py-1 px-2 text-danger m-0">
                                                                            {msg}
                                                                        </p>
                                                                    )}
                                                                </ErrorMessage>
                                                            </Col>
                                                        </Row>
                                                        {isWithWarehouse && (
                                                            <Row className={"mt-3"}>
                                                                <Col
                                                                    xl={2}
                                                                    className={"d-flex align-items-center mb-2 mb-xl-0"}
                                                                >
                                                                    <label
                                                                        htmlFor="createNewBooking-technologicalMap-field"
                                                                        className="text-dark fs-7 fw-bold"
                                                                    >
                                                                        {translate("Technological Map")}
                                                                    </label>
                                                                </Col>
                                                                <Col xl={10} className={"position-relative"}>
                                                                    <ReactSelect
                                                                        options={technologicalMaps}
                                                                        value={null}
                                                                        onChange={(value: ReactSelectInterface) => {
                                                                            setSelectedTechnologicalMaps([
                                                                                ...selectedTechnologicalMaps,
                                                                                value,
                                                                            ]);
                                                                            setTechnologicalMaps(
                                                                                technologicalMaps.filter(
                                                                                    (
                                                                                        technologicalMap: ReactSelectInterface
                                                                                    ) =>
                                                                                        technologicalMap.value !==
                                                                                        value.value
                                                                                )
                                                                            );
                                                                        }}
                                                                        placeholder={translate(
                                                                            "Select Technological Map"
                                                                        )}
                                                                        isSearchable
                                                                        name={"technologicalMap"}
                                                                        id={"createNewBooking-technologicalMap-field"}
                                                                        disabled={!values.service.serviceId}
                                                                    />
                                                                    {loadingSelectedTechnologicalMaps ||
                                                                        (loadingService && (
                                                                            <div
                                                                                className={
                                                                                    "position-absolute top-50 justify-content-center align-items-center end-0 me-5 pe-3 translate-middle-y"
                                                                                }
                                                                            >
                                                                                <DotLoadingComponent size={4} />
                                                                            </div>
                                                                        ))}
                                                                </Col>

                                                                {selectedTechnologicalMaps.length !== 0 && (
                                                                    <Col xl={{ span: 10, offset: 2 }}>
                                                                        {selectedTechnologicalMaps.map(
                                                                            (selectedTechnologicalMap) => {
                                                                                return (
                                                                                    <div
                                                                                        className={
                                                                                            "ps-2 ms-1 mt-3 d-flex align-items-center justify-content-between"
                                                                                        }
                                                                                        key={
                                                                                            selectedTechnologicalMap.value
                                                                                        }
                                                                                    >
                                                                                        {selectedTechnologicalMap.label}
                                                                                        <button
                                                                                            type="button"
                                                                                            className=" text-dark ms-2  px-3 border square-btn btn btn-outline-gray"
                                                                                            onClick={() => {
                                                                                                deleteSelectedTechnologicalMaps(
                                                                                                    selectedTechnologicalMap
                                                                                                );
                                                                                            }}
                                                                                        >
                                                                                            <i className="bi bi-dash-lg"></i>
                                                                                        </button>
                                                                                    </div>
                                                                                );
                                                                            }
                                                                        )}
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                        )}
                                                        <Row className={"mt-3"}>
                                                            <Col
                                                                xl={2}
                                                                className={"d-flex align-items-center mb-2 mb-xl-0"}
                                                            >
                                                                <label
                                                                    htmlFor="createNewBooking-employeeSelect-field required-label"
                                                                    className="text-dark fs-7 fw-bold"
                                                                >
                                                                    {translate("Master")}
                                                                </label>
                                                            </Col>
                                                            <Col xl={10} className={"position-relative"}>
                                                                <ReactSelect
                                                                    options={scopedEmployeeList.map((employee) => ({
                                                                        label: AddRecordService.buildEmployeeLabel(
                                                                            employee,
                                                                            translate
                                                                        ),
                                                                        value: employee.id,
                                                                    }))}
                                                                    value={
                                                                        values.employeeId
                                                                            ? AddRecordService.getEmployeeForSelect(
                                                                                  values.employeeId,
                                                                                  scopedEmployeeList,
                                                                                  translate
                                                                              )
                                                                            : null
                                                                    }
                                                                    onChange={(value: ReactSelectInterface) => {
                                                                        const currentEmployee = scopedEmployeeList.find(
                                                                            (employee) => employee.id === value.value
                                                                        );

                                                                        if (!currentEmployee) return;

                                                                        const updatedValues = {
                                                                            ...values,
                                                                            employeeId: value.value,
                                                                            price: values.service.serviceId
                                                                                ? currentEmployee
                                                                                      .employeeServiceRelation.price
                                                                                : values.price,
                                                                            service: values.service.serviceId
                                                                                ? {
                                                                                      ...values.service,
                                                                                      duration:
                                                                                          currentEmployee
                                                                                              .employeeServiceRelation
                                                                                              .duration,
                                                                                  }
                                                                                : values.service,
                                                                            endTime:
                                                                                values.startTime &&
                                                                                values.service.serviceId
                                                                                    ? AddRecordService.calcEndTime(
                                                                                          values.startTime,
                                                                                          currentEmployee
                                                                                              .employeeServiceRelation
                                                                                              .duration
                                                                                      )
                                                                                    : null,
                                                                        };

                                                                        setValues(updatedValues);
                                                                        setPickedEmployeeId(value.value);
                                                                    }}
                                                                    placeholder={translate("Choose Master")}
                                                                    isSearchable
                                                                    name={"createNewBooking-employeeSelect-field"}
                                                                    disabled={
                                                                        shouldRestrictCreateBookingToSelf ||
                                                                        (pickedDate &&
                                                                            !pickedTime &&
                                                                            !pickedEmployeeId) ||
                                                                        loadingEmployees
                                                                    }
                                                                    noOptionsMessage={
                                                                        !loadingEmployees &&
                                                                        scopedEmployeeList.length === 0 &&
                                                                        masterEmptyState
                                                                            ? () => masterEmptyState
                                                                            : undefined
                                                                    }
                                                                />
                                                                {loadingTimes ||
                                                                    loadingDates ||
                                                                    loadingEmployees ||
                                                                    (loadingService && (
                                                                        <div
                                                                            className={
                                                                                "position-absolute justify-content-center align-items-center top-50 end-0 me-5 pe-3 translate-middle-y"
                                                                            }
                                                                        >
                                                                            <DotLoadingComponent size={4} />
                                                                        </div>
                                                                    ))}
                                                                {values.employeeId && (
                                                                    <ClearValueBtn
                                                                        onClearValue={onEmployeeClearValue}
                                                                    />
                                                                )}
                                                            </Col>
                                                            <Col xl={{ span: 10, offset: 2 }}>
                                                                <ErrorMessage name="employeeId">
                                                                    {(msg) => (
                                                                        <p className="fs-7 py-1 px-2 text-danger m-0">
                                                                            {msg}
                                                                        </p>
                                                                    )}
                                                                </ErrorMessage>
                                                            </Col>
                                                        </Row>
                                                        <Row className={"mt-3"}>
                                                            <Col
                                                                xl={2}
                                                                className={"d-flex align-items-center mb-2 mb-xl-0"}
                                                            >
                                                                <label className="text-dark fs-7 fw-bold">
                                                                    {translate("Price")}
                                                                </label>
                                                            </Col>
                                                            <Col xl={10}>
                                                                <p className={"mb-0 fs-7"}>
                                                                    {moduleData.user.company.currency.currencySign}
                                                                    <span
                                                                        className={`ms-2 ${values.price === 0 ? "text-secondary" : ""}`}
                                                                    >
                                                                        {values.price}
                                                                    </span>
                                                                </p>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </section>
                                                <section className={"my-5"}>
                                                    <h4 className={"mb-3"}>{translate("dateAndTime")}</h4>
                                                    <div className={"px-0 pe-lg-6 ms-0"}>
                                                        <Row>
                                                            <Col xl={2} className={"d-flex align-items-center"}>
                                                                <label
                                                                    htmlFor="createNewBooking-recordDate-field"
                                                                    className="text-dark fs-7 fw-bold required-label mb-2 mb-xl-0"
                                                                >
                                                                    {translate("Date of Record")}
                                                                </label>
                                                            </Col>
                                                            <Col xl={10}>
                                                                <div className="position-relative">
                                                                    <DateView
                                                                        selected={pickedDate}
                                                                        id={"createNewBooking-recordDate-field"}
                                                                        className={`form-control  w-100 ${!!(touched.pickedDate && errors.pickedDate) ? "border-danger" : ""}`}
                                                                        placeholderText={translate("dd.mm.yyyy")}
                                                                        onChange={(date) => {
                                                                            const updateNotification =
                                                                                AddRecordService.notificationValueCalc(
                                                                                    values.notification,
                                                                                    DateTime.fromJSDate(date),
                                                                                    translate
                                                                                );
                                                                            const notificationToString =
                                                                                AddRecordService.notificationValueToString(
                                                                                    updateNotification,
                                                                                    DateTime.fromJSDate(date)
                                                                                );
                                                                            const status: StatusPickerInner =
                                                                                AddRecordService.statusService(
                                                                                    values.status,
                                                                                    AddRecordService.statusToShow(
                                                                                        statusData.bookingStatus,
                                                                                        DateTime.fromJSDate(date),
                                                                                        bookingData?.booking.status.id
                                                                                    )
                                                                                );

                                                                            const updatedValues: recordFormValueType = {
                                                                                ...values,
                                                                                pickedDate: date,
                                                                                status: canEditStatusInForm
                                                                                    ? {
                                                                                          ...values.status,
                                                                                          statusId: status.id,
                                                                                      }
                                                                                    : values.status,
                                                                                notification: {
                                                                                    ...values.notification,
                                                                                    date: notificationToString,
                                                                                },
                                                                            };
                                                                            setValues(updatedValues);
                                                                            setPickedTime(null);
                                                                            setPickedDate(date);
                                                                        }}
                                                                        wrapperClassName="w-100 position-relative"
                                                                        dateFormat="dd.MM.yyyy"
                                                                        filterDate={isDisabledDate}
                                                                        disabled={
                                                                            loadingTimes ||
                                                                            loadingDates ||
                                                                            loadingEmployees ||
                                                                            loadingService
                                                                        }
                                                                    />
                                                                    <label
                                                                        htmlFor="createNewBooking-recordDate-field"
                                                                        className={
                                                                            "position-absolute d-flex top-50 translate-middle-y end-0 pe-2 me-1"
                                                                        }
                                                                    >
                                                                        <i className="bi bi-calendar4-week d-flex"></i>
                                                                    </label>
                                                                    {loadingTimes ||
                                                                        loadingDates ||
                                                                        loadingEmployees ||
                                                                        (loadingService && (
                                                                            <div
                                                                                className={
                                                                                    "position-absolute d-flex justify-content-center align-items-center top-50 end-0 me-5 pe-3 translate-middle-y"
                                                                                }
                                                                            >
                                                                                <DotLoadingComponent size={4} />
                                                                            </div>
                                                                        ))}
                                                                    {values.pickedDate && (
                                                                        <ClearValueBtn
                                                                            onClearValue={onDateTimeClearValues}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </Col>
                                                            <Col xl={{ span: 10, offset: 2 }}>
                                                                <ErrorMessage name="pickedDate">
                                                                    {(msg) => (
                                                                        <p className="fs-7 py-1 px-2 text-danger m-0">
                                                                            {msg}
                                                                        </p>
                                                                    )}
                                                                </ErrorMessage>
                                                            </Col>
                                                        </Row>
                                                        <Row className={"mt-3"}>
                                                            <Col
                                                                xl={2}
                                                                className={"d-flex align-items-center mb-2 mb-xl-0"}
                                                            >
                                                                <label
                                                                    htmlFor="createNewBooking-startTime-field"
                                                                    onClick={() => {
                                                                        const inputElement =
                                                                            timePickerDivRef.current?.querySelector(
                                                                                "input"
                                                                            );
                                                                        inputElement?.focus();
                                                                    }}
                                                                    className="text-dark fs-7 fw-bold required-label"
                                                                >
                                                                    {translate("Time of Record")}
                                                                </label>
                                                            </Col>
                                                            <Col xl={10} className={"time-picker-wrapper"}>
                                                                <div className={"d-flex align-items-center"}>
                                                                    <div className="position-relative w-100">
                                                                        <TimePickerWrapper
                                                                            name={"startTime"}
                                                                            startValue={
                                                                                values.startTime
                                                                                    ? values.startTime.toFormat("HH:mm")
                                                                                    : ""
                                                                            }
                                                                            onChange={onTimePickHandler}
                                                                            isDisabled={
                                                                                !pickedDate ||
                                                                                loadingTimes ||
                                                                                loadingEmployees ||
                                                                                loadingService
                                                                            }
                                                                            inactiveIntervals={bookingTimes}
                                                                            step={15}
                                                                            id={"createNewBooking-startTime-field"}
                                                                            ref={timePickerDivRef}
                                                                            isDurationMoreThenGap={durationMoreThenGap}
                                                                            isLoading={
                                                                                loadingTimes ||
                                                                                loadingDates ||
                                                                                loadingEmployees ||
                                                                                loadingService
                                                                            }
                                                                        />
                                                                    </div>
                                                                    <i className="bi bi-dash d-flex px-3"></i>
                                                                    <div className="position-relative w-100">
                                                                        <TimePickerWrapper
                                                                            name={"endTime"}
                                                                            startValue={
                                                                                values.endTime
                                                                                    ? values.endTime.toFormat("HH:mm")
                                                                                    : ""
                                                                            }
                                                                            onChange={onTimePickHandler}
                                                                            isDisabled={true}
                                                                            isLoading={
                                                                                loadingTimes ||
                                                                                loadingDates ||
                                                                                loadingEmployees ||
                                                                                loadingService
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                            <Col xl={{ span: 10, offset: 2 }}>
                                                                <ErrorMessage name="startTime">
                                                                    {(msg) => (
                                                                        <p className="fs-7 py-1 px-2 text-danger m-0">
                                                                            {msg}
                                                                        </p>
                                                                    )}
                                                                </ErrorMessage>
                                                            </Col>
                                                            {durationMoreThenGap && (
                                                                <Col xl={{ span: 10, offset: 2 }}>
                                                                    <span className={"text-orange fs-7 mt-2"}>
                                                                        {translate(
                                                                            "The service duration has been increased. You can choose another day and time for your appointment, or keep it as it is."
                                                                        )}
                                                                    </span>
                                                                </Col>
                                                            )}
                                                        </Row>
                                                        <Row className={"mt-3"}>
                                                            <Col
                                                                xl={2}
                                                                className={"d-flex align-items-center mb-2 mb-xl-0"}
                                                            >
                                                                <label
                                                                    htmlFor="createNewBooking-recordStatus-field"
                                                                    className="text-dark fs-7 fw-bold required-label"
                                                                >
                                                                    {translate("Visit Status")}
                                                                </label>
                                                            </Col>
                                                            <Col xl={10}>
                                                                <StatusPicker
                                                                    statusList={statusList}
                                                                    selectedDate={DateTime.fromJSDate(pickedDate)}
                                                                    disabled={!canEditStatusInForm}
                                                                    value={AddRecordService.statusService(
                                                                        values.status,
                                                                        AddRecordService.statusToShow(
                                                                            statusData.bookingStatus,
                                                                            pickedDate
                                                                                ? DateTime.fromJSDate(pickedDate)
                                                                                : DateTime.now().plus({ second: 10 }),
                                                                            bookingData?.booking.status.id
                                                                        )
                                                                    )}
                                                                    onChange={(statusId: number) => {
                                                                        if (!canEditStatusInForm) return;
                                                                        setFieldValue("status", {
                                                                            isSelected: true,
                                                                            statusId: statusId,
                                                                        });
                                                                    }}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </section>
                                                <section className={"my-5"}>
                                                    <h4 className={"mb-3"}>
                                                        {translate("Information about the Client")}
                                                    </h4>
                                                    <div className={"px-0 pe-lg-6 ms-0"}>
                                                        <Row>
                                                            <Col
                                                                xl={2}
                                                                className={"d-flex align-items-center mb-2 mb-xl-0"}
                                                            >
                                                                <label
                                                                    htmlFor="createNewBooking-phone-field"
                                                                    className="text-dark fs-7 fw-bold required-label"
                                                                >
                                                                    {translate("Client Contacts")}
                                                                </label>
                                                            </Col>
                                                            <Col xl={10}>
                                                                <div className="position-relative">
                                                                    <PhoneInput
                                                                        defaultCountry={moduleData.user.company.country.isoCode.toLowerCase()}
                                                                        name={"phone"}
                                                                        className="fs-7 height-100 w-100"
                                                                        placeholder={translate("Enter phone number")}
                                                                        inputClassName={`form-control fs-7 rounded-1 text-truncate ${touched.phone && errors.phone ? "border-danger" : "border-secondary"}`}
                                                                        inputStyle={{
                                                                            height: "50px",
                                                                            paddingLeft: "65px",
                                                                            fontSize: "16px",
                                                                        }}
                                                                        countrySelectorStyleProps={{
                                                                            className: "position-absolute",
                                                                            style: { height: "50px" },
                                                                            buttonClassName: `h-100 rounded-start-1 ps-2 pe-1 ${touched.phone && errors.phone ? "border-danger" : "border-secondary"}`,
                                                                        }}
                                                                        value={values.phone}
                                                                        onChange={(phone, meta) => {
                                                                            debouncedSendQuery("phone", phone);
                                                                            phone.slice(1) !== meta.country.dialCode
                                                                                ? setFieldValue("phone", phone)
                                                                                : "";
                                                                        }}
                                                                        inputProps={{
                                                                            id: "createNewBooking-phone-field",
                                                                        }}
                                                                        disabled={AddRecordService.disableClientContacts(
                                                                            bookingUuid,
                                                                            values.status.statusId
                                                                        )}
                                                                    />
                                                                </div>
                                                            </Col>
                                                            <Col xl={{ span: 10, offset: 2 }}>
                                                                <ErrorMessage name="phone">
                                                                    {(msg) => (
                                                                        <p className="fs-7 py-1 px-2 text-danger m-0">
                                                                            {msg}
                                                                        </p>
                                                                    )}
                                                                </ErrorMessage>
                                                            </Col>
                                                            <Col xl={{ span: 10, offset: 2 }}>
                                                                <div className="text-secondary text-center my-2">
                                                                    <p className="position-relative or-dash mb-0">
                                                                        {translate("or")}
                                                                    </p>
                                                                </div>
                                                            </Col>
                                                            <Col xl={{ span: 10, offset: 2 }}>
                                                                <Field name="email">
                                                                    {({
                                                                        field,
                                                                        form: { touched, errors, setFieldValue },
                                                                    }: FieldProps) => (
                                                                        <>
                                                                            <input
                                                                                {...field}
                                                                                placeholder={translate("Add E-mail")}
                                                                                className={`form-control rounded-1 lh-22 ${
                                                                                    touched.email && errors.email
                                                                                        ? "border-danger"
                                                                                        : ""
                                                                                }`}
                                                                                onChange={(
                                                                                    e: ChangeEvent<HTMLInputElement>
                                                                                ) => {
                                                                                    setFieldValue(
                                                                                        "email",
                                                                                        e.target.value
                                                                                    );
                                                                                    debouncedSendQuery(
                                                                                        "email",
                                                                                        e.target.value
                                                                                    );
                                                                                }}
                                                                                disabled={AddRecordService.disableClientContacts(
                                                                                    bookingUuid,
                                                                                    values.status.statusId
                                                                                )}
                                                                            />
                                                                        </>
                                                                    )}
                                                                </Field>
                                                                <ErrorMessage name="email">
                                                                    {(msg) => (
                                                                        <p className="fs-7 p-1 text-danger m-0">
                                                                            {msg}
                                                                        </p>
                                                                    )}
                                                                </ErrorMessage>
                                                            </Col>
                                                        </Row>
                                                        <Row className={"mt-3"}>
                                                            <Col
                                                                xl={2}
                                                                className={"d-flex align-items-center mb-2 mb-xl-0"}
                                                            >
                                                                <label
                                                                    htmlFor="createNewBooking-clientName-field"
                                                                    className="text-dark fs-7 fw-bold required-label"
                                                                >
                                                                    {translate("Client name")}
                                                                </label>
                                                            </Col>
                                                            <Col xl={10}>
                                                                <Field
                                                                    name="clientName"
                                                                    maxLength={50}
                                                                    id={"createNewBooking-clientName-field"}
                                                                    placeholder={translate("Add Name")}
                                                                    className={`form-control rounded-1 lh-22 ${
                                                                        touched.clientName && errors.clientName
                                                                            ? "border-danger"
                                                                            : ""
                                                                    }`}
                                                                />
                                                            </Col>
                                                            <Col xl={{ span: 10, offset: 2 }}>
                                                                <ErrorMessage name="clientName">
                                                                    {(msg) => (
                                                                        <p className="fs-7 p-1 text-danger m-0">
                                                                            {msg}
                                                                        </p>
                                                                    )}
                                                                </ErrorMessage>
                                                            </Col>
                                                        </Row>
                                                        <Row className={"mt-3"}>
                                                            <Col
                                                                xl={2}
                                                                className={"d-flex align-items-center mb-2 mb-xl-0"}
                                                            >
                                                                <label
                                                                    htmlFor="createNewBooking-clientMiddleName-field"
                                                                    className="text-dark fs-7 fw-bold"
                                                                >
                                                                    {translate("Client Middle name")}
                                                                </label>
                                                            </Col>
                                                            <Col xl={10}>
                                                                <Field
                                                                    name="clientMiddleName"
                                                                    maxLength={50}
                                                                    id={"createNewBooking-clientMiddleName-field"}
                                                                    placeholder={translate("Add Middle name")}
                                                                    className={`form-control rounded-1 lh-22 ${
                                                                        touched.clientMiddleName &&
                                                                        errors.clientMiddleName
                                                                            ? "border-danger"
                                                                            : ""
                                                                    }`}
                                                                />
                                                            </Col>
                                                            <Col xl={{ span: 10, offset: 2 }}>
                                                                <ErrorMessage name="clientMiddleName">
                                                                    {(msg) => (
                                                                        <p className="fs-7 p-1 text-danger m-0">
                                                                            {msg}
                                                                        </p>
                                                                    )}
                                                                </ErrorMessage>
                                                            </Col>
                                                        </Row>
                                                        <Row className={"mt-3"}>
                                                            <Col
                                                                xl={2}
                                                                className={"d-flex align-items-center mb-2 mb-xl-0"}
                                                            >
                                                                <label
                                                                    htmlFor="createNewBooking-clientLastName-field"
                                                                    className="text-dark fs-7 fw-bold"
                                                                >
                                                                    {translate("Client Last name")}
                                                                </label>
                                                            </Col>
                                                            <Col xl={10}>
                                                                <Field
                                                                    name="clientLastName"
                                                                    maxLength={50}
                                                                    id={"createNewBooking-clientLastName-field"}
                                                                    placeholder={translate("Add Last name")}
                                                                    className={`form-control rounded-1 lh-22 ${
                                                                        touched.clientLastName && errors.clientLastName
                                                                            ? "border-danger"
                                                                            : ""
                                                                    }`}
                                                                />
                                                            </Col>
                                                            <Col xl={{ span: 10, offset: 2 }}>
                                                                <ErrorMessage name="clientMiddleName">
                                                                    {(msg) => (
                                                                        <p className="fs-7 p-1 text-danger m-0">
                                                                            {msg}
                                                                        </p>
                                                                    )}
                                                                </ErrorMessage>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </section>
                                                <section className={"my-5"}>
                                                    <div className={"px-0 pe-lg-6 ms-0"}>
                                                        <Row className={"mt-3"}>
                                                            <Col xl={2} className={"d-flex mb-2 mb-xl-0"}>
                                                                <label
                                                                    htmlFor="createNewBooking-notes-field"
                                                                    className="text-dark fs-7 fw-bold pt-2 mt-1"
                                                                >
                                                                    {translate("Notes")}
                                                                </label>
                                                            </Col>
                                                            <Col xl={10}>
                                                                <Field
                                                                    as="textarea"
                                                                    name="notes"
                                                                    className="form-control"
                                                                    id={"createNewBooking-notes-field"}
                                                                    maxLength={200}
                                                                    placeholder={translate(
                                                                        "Enter notes for the booking"
                                                                    )}
                                                                />
                                                                <ErrorMessage name="notes">
                                                                    {(msg) => (
                                                                        <p className="fs-7 m-0 p-1 text-danger">
                                                                            {msg}
                                                                        </p>
                                                                    )}
                                                                </ErrorMessage>
                                                                <span className="text-grey fs-7 mt-1 d-block">
                                                                    {values.notes.length} / 200
                                                                </span>
                                                            </Col>
                                                        </Row>
                                                        <Row className={"mt-3"}>
                                                            <Col
                                                                xl={2}
                                                                className={"d-flex align-items-center mb-2 mb-xl-0"}
                                                            >
                                                                <label
                                                                    htmlFor="notification"
                                                                    className="text-dark fs-7 fw-bold required-label"
                                                                >
                                                                    {translate("Notification")}
                                                                </label>
                                                            </Col>
                                                            <Col xl={10}>
                                                                <NotificationSelect
                                                                    name={"notification"}
                                                                    isDisabled={!(!!pickedDate && !!pickedTime)}
                                                                    value={AddRecordService.notificationValueCalc(
                                                                        values.notification,
                                                                        DateTime.fromJSDate(pickedDate),
                                                                        translate
                                                                    )}
                                                                    options={AddRecordService.notificationSelectOptions(
                                                                        DateTime.fromJSDate(pickedDate),
                                                                        translate
                                                                    )}
                                                                    onChange={(selectedValue: ReactSelectInterface) => {
                                                                        const valueToString =
                                                                            AddRecordService.notificationValueToString(
                                                                                selectedValue,
                                                                                DateTime.fromJSDate(pickedDate)
                                                                            );
                                                                        setFieldValue("notification", {
                                                                            isUserPick: true,
                                                                            date: valueToString,
                                                                        });
                                                                    }}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </section>
                                                <Row className="pe-lg-6">
                                                    <Col xs={12} sm={6} md={4} xl={3} className={"mb-3 mb-md-0"}>
                                                        <Button
                                                            variant="outline-primary"
                                                            className="w-100"
                                                            onClick={() => {
                                                                navigate(`/logbook/journal/${branchId}`, {
                                                                    state: {
                                                                        toast: false,
                                                                        isNeedToWriteOff: false,
                                                                    },
                                                                });
                                                            }}
                                                        >
                                                            {translate("cancel")}
                                                        </Button>
                                                    </Col>
                                                    <Col xs={12} sm={6} md={4} xl={3} className={"mb-3 mb-md-0"}>
                                                        <Button
                                                            variant="primary"
                                                            type="submit"
                                                            className="w-100"
                                                            disabled={
                                                                isSubmitting || (isEditMode && !canEditBookingByAccess)
                                                            }
                                                        >
                                                            {isSubmitting ? (
                                                                <SpinnerLoader
                                                                    variant="light"
                                                                    loadingText={
                                                                        bookingUuid
                                                                            ? translate("Editing...")
                                                                            : translate("Adding...")
                                                                    }
                                                                />
                                                            ) : bookingUuid ? (
                                                                translate("EditRecord")
                                                            ) : (
                                                                translate("Add")
                                                            )}
                                                        </Button>
                                                    </Col>
                                                    {bookingUuid && canViewHistoryByAccess && (
                                                        <Col sm={12} md={4} xl={3} className={"ms-auto"}>
                                                            <Button
                                                                variant="success"
                                                                type="button"
                                                                className="w-100 text-truncate"
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/logbook/edit-record/${bookingUuid}/change-history/${branchId}`
                                                                    )
                                                                }
                                                            >
                                                                {translate("History of Changes")}
                                                            </Button>
                                                        </Col>
                                                    )}
                                                </Row>
                                            </div>
                                        </Form>
                                    </>
                                )
                            );
                        }}
                    </Formik>
                    <NotificationsModal
                        show={checkModalShow}
                        handleClose={closeCheckModal}
                        title="To create records, follow these steps"
                    />
                    {bookingUuid && toggleProductSaleModal && (
                        <SaleProducts
                            showModal={toggleProductSaleModal}
                            handleCloseModal={handleToggleProductSaleModal}
                            action={isBookingSale ? "update" : "create"}
                            bookingDataForSale={bookingDataForSale}
                            bookingUuid={bookingUuid}
                            toggleShowToast={toggleShowToast}
                            bookingRefetch={() => {
                                if (isBookingSale) {
                                    infoForSalesTooltipRefetch();
                                } else {
                                    checkBookingForSaleRefetch();
                                }
                            }}
                        />
                    )}
                    {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
                </>
            )}
        </>
    );
};
